import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import Loading from '@/components/base/Loading';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import { useToast } from '@/components/feature/ToastProvider';
import { deleteProject, getProjectRelations, type ProjectRelations } from '@/lib/projectService';
import type { Project } from '@/types';

interface ProjectDeleteDialogProps {
  project: Project | null;
  onClose: () => void;
  onDeleted: () => void;
}

export default function ProjectDeleteDialog({ project, onClose, onDeleted }: ProjectDeleteDialogProps) {
  const { notify } = useToast();
  const [loading, setLoading] = useState(false);
  const [relations, setRelations] = useState<ProjectRelations | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!project) {
      setRelations(null);
      return undefined;
    }
    let active = true;
    setLoading(true);
    getProjectRelations(project.id)
      .then((result) => {
        if (active) setRelations(result);
      })
      .catch(() => {
        if (active) setRelations({ receipts: 0, movements: 0 });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [project]);

  if (!project) return null;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteProject(project);
      notify({
        tone: 'success',
        title: 'Proyecto eliminado',
        message: `${project.name} fue eliminado correctamente.`,
      });
      onDeleted();
    } catch {
      notify({
        tone: 'error',
        title: 'No se pudo eliminar',
        message: 'Verifique que el proyecto no tenga registros asociados e intente nuevamente.',
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Modal
        open
        title="Verificando información relacionada"
        description="Comprobando recibos y movimientos financieros asociados..."
        icon="ri-loader-4-line"
        onClose={onClose}
      >
        <Loading label="Un momento por favor..." />
      </Modal>
    );
  }

  const receipts = relations?.receipts ?? 0;
  const movements = relations?.movements ?? 0;

  if (receipts > 0) {
    return (
      <Modal
        open
        title="No se puede eliminar este proyecto"
        description="Existen registros asociados que deben conservarse."
        icon="ri-error-warning-line"
        tone="warning"
        onClose={onClose}
        footer={
          <Button variant="outline" onClick={onClose}>
            Entendido
          </Button>
        }
      >
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3.5 text-sm text-amber-800">
          <p>
            El proyecto <strong>{project.name}</strong> tiene{' '}
            <strong>{receipts}</strong> recibo(s) emitido(s)
            {movements > 0 ? ` y ${movements} movimiento(s) financiero(s)` : ''} asociado(s).
          </p>
          <p className="mt-2">
            Para mantener la consistencia de la información contable no se permite eliminarlo. Si el
            proyecto ya no está activo, cambie su estado a{' '}
            <strong>Desactivado</strong> en lugar de eliminarlo.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <ConfirmDialog
      open
      title="¿Está seguro de que desea eliminar este proyecto?"
      description="Esta acción eliminará la información asociada al proyecto y no podrá deshacerse."
      confirmLabel="Eliminar"
      cancelLabel="Cancelar"
      tone="danger"
      icon="ri-delete-bin-6-line"
      loading={deleting}
      onConfirm={handleDelete}
      onCancel={onClose}
    />
  );
}